import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Button, Space, message } from 'antd';
import { ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import { leavesApi, show422Error, ValidationError } from '../../../utils/api';
import {
  unwrapLeaveRecord,
  durationApiToFormValue,
  apiDateToJalali,
} from '../../../utils/leaveDurationUtils';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import LeaveForm from '../../../components/hr/leaves/LeaveForm';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';

const EditLeave = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/leaves', { state: buildGoBackState() });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [leave, setLeave] = useState(null);

  useEffect(() => {
    const fetchLeave = async () => {
      setFetching(true);
      try {
        const res = await leavesApi.getById(id);
        setLeave(unwrapLeaveRecord(res));
      } catch (error) {
        console.error('خطا در دریافت اطلاعات مرخصی:', error);
        message.error('خطا در دریافت اطلاعات مرخصی');
        goBackToList();
      } finally {
        setFetching(false);
      }
    };
    fetchLeave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const initialValues = useMemo(() => {
    if (!leave) return null;
    const duration = durationApiToFormValue(leave.duration, leave.half_day_type);
    const leaveDateJalali = apiDateToJalali(leave.leave_date || leave.date);
    const multiStart = apiDateToJalali(leave.multi_start_date || leave.multiStartDate);
    const multiEnd = apiDateToJalali(leave.multi_end_date || leave.multiEndDate);

    return {
      member: leave.user_id,
      leaveType: leave.leave_type_id,
      status: leave.status || 'pending',
      duration,
      date: leaveDateJalali,
      dateRange:
        duration === 'multiple' && multiStart && multiEnd ? [multiStart, multiEnd] : undefined,
      start_time: leave.start_time || null,
      end_time: leave.end_time || null,
      reason: leave.reason || '',
    };
  }, [leave]);

  const handleSave = async (payload) => {
    setLoading(true);
    try {
      await leavesApi.update(id, payload);
      message.success('مرخصی با موفقیت ویرایش شد');
      goBackToList();
    } catch (error) {
      console.error('خطا در ویرایش مرخصی:', error);
      if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error);
        throw error;
      }
      message.error(error?.message || 'خطا در ویرایش مرخصی');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !initialValues) {
    return (
      <MainLayout>
        <div style={{ fontFamily: 'CustomFont' }}>
          <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
            <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <EditOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                    ویرایش مرخصی
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">در حال بارگذاری...</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
              <FormSkeleton />
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <EditOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  ویرایش مرخصی
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">ویرایش درخواست مرخصی</p>
              </div>
            </div>
            <Space>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={goBackToList}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <LeaveForm
                key={id}
                initialValues={initialValues}
                onSave={handleSave}
                onCancel={goBackToList}
                loading={loading}
                submitLabel="ذخیره تغییرات"
              />
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditLeave;
